import { useState } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  ListInput,
  Button,
  Toggle,
  Range,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  Gamepad2,
  Percent,
  DollarSign,
  ArrowUpDown,
  Eye,
  PlayCircle,
  Trophy,
  Zap,
  ShieldCheck,
  Wallet,
  ArrowDownToLine,
  CalendarClock,
  Globe,
  KeyRound,
  Wifi,
  Timer,
  Clock,
  Save,
  CheckCircle2,
} from 'lucide-react'

// --- Types ---

interface GameConfig {
  rakePercent: number
  maxRakeAmount: string
  minBuyInMultiplier: string
  maxBuyInMultiplier: string
  autoStart: boolean
  spectatorMode: boolean
}

interface JackpotConfig {
  contributionPercent: number
  minTriggerHand: string
  poolCap: string
}

interface SecurityConfig {
  maxDeposit: string
  maxWithdrawal: string
  dailyWithdrawalLimit: string
  ipRestriction: boolean
  twoFactorAuth: boolean
}

interface SystemConfig {
  wsHeartbeat: string
  disconnectTimeout: string
  handTimeout: string
}

// --- Component ---

export function AdminConfigScreen() {
  // Game settings
  const [game, setGame] = useState<GameConfig>({
    rakePercent: 5,
    maxRakeAmount: '50',
    minBuyInMultiplier: '20',
    maxBuyInMultiplier: '100',
    autoStart: true,
    spectatorMode: true,
  })

  // Jackpot settings
  const [jackpot, setJackpot] = useState<JackpotConfig>({
    contributionPercent: 1,
    minTriggerHand: '同花順',
    poolCap: '100000',
  })

  // Security settings
  const [security, setSecurity] = useState<SecurityConfig>({
    maxDeposit: '10000',
    maxWithdrawal: '5000',
    dailyWithdrawalLimit: '20000',
    ipRestriction: false,
    twoFactorAuth: true,
  })

  // System settings
  const [system, setSystem] = useState<SystemConfig>({
    wsHeartbeat: '30',
    disconnectTimeout: '60',
    handTimeout: '30',
  })

  const [showToast, setShowToast] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)

  const handleSave = () => {
    setShowSaveDialog(false)
    setShowToast(true)
    console.log('[Admin] Config saved:', { game, jackpot, security, system })
    setTimeout(() => setShowToast(false), 2000)
  }

  return (
    <>
      <Navbar title="系統設定" />

      {/* ========== Game Settings ========== */}
      <BlockTitle className="flex items-center gap-2">
        <Gamepad2 className="w-4 h-4 text-[#007AFF]" />
        遊戲設定
      </BlockTitle>

      <List strongIos insetIos>
        {/* Rake Percent */}
        <ListItem
          title={
            <div className="flex items-center justify-between">
              <span className="font-medium">預設抽水比例</span>
              <span className="text-[#007AFF] font-semibold tabular-nums">
                {game.rakePercent}%
              </span>
            </div>
          }
          subtitle={
            <div className="mt-2">
              <Range
                value={game.rakePercent}
                min={1}
                max={10}
                step={0.5}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setGame((prev) => ({ ...prev, rakePercent: parseFloat(e.target.value) }))
                }
              />
              <div className="flex justify-between text-[11px] text-[#C7C7CC] mt-1">
                <span>1%</span>
                <span>10%</span>
              </div>
            </div>
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center">
              <Percent className="w-4 h-4 text-[#007AFF]" />
            </div>
          }
        />

        {/* Max Rake Amount */}
        <ListInput
          label="最大抽水金額"
          type="number"
          placeholder="50"
          value={game.maxRakeAmount}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setGame((prev) => ({ ...prev, maxRakeAmount: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-[#34C759]" />
            </div>
          }
          info="單位：USD"
        />

        {/* Min Buy-in Multiplier */}
        <ListInput
          label="最小買入倍數"
          type="number"
          placeholder="20"
          value={game.minBuyInMultiplier}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setGame((prev) => ({ ...prev, minBuyInMultiplier: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center">
              <ArrowUpDown className="w-4 h-4 text-[#FF9500]" />
            </div>
          }
          info="x BB"
        />

        {/* Max Buy-in Multiplier */}
        <ListInput
          label="最大買入倍數"
          type="number"
          placeholder="100"
          value={game.maxBuyInMultiplier}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setGame((prev) => ({ ...prev, maxBuyInMultiplier: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center">
              <ArrowUpDown className="w-4 h-4 text-[#FF9500]" />
            </div>
          }
          info="x BB"
        />

        {/* Auto Start */}
        <ListItem
          media={
            <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center">
              <PlayCircle className="w-4 h-4 text-[#34C759]" />
            </div>
          }
          title={<span className="font-medium">自動開桌</span>}
          after={
            <Toggle
              checked={game.autoStart}
              onChange={() =>
                setGame((prev) => ({ ...prev, autoStart: !prev.autoStart }))
              }
            />
          }
        />

        {/* Spectator Mode */}
        <ListItem
          media={
            <div className="w-8 h-8 rounded-lg bg-[#8E8E93]/15 flex items-center justify-center">
              <Eye className="w-4 h-4 text-[#8E8E93]" />
            </div>
          }
          title={<span className="font-medium">旁觀者模式</span>}
          after={
            <Toggle
              checked={game.spectatorMode}
              onChange={() =>
                setGame((prev) => ({ ...prev, spectatorMode: !prev.spectatorMode }))
              }
            />
          }
        />
      </List>

      {/* ========== Jackpot Settings ========== */}
      <BlockTitle className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-[#FFD700]" />
        累積獎金設定
      </BlockTitle>

      <List strongIos insetIos>
        {/* Contribution Percent */}
        <ListItem
          title={
            <div className="flex items-center justify-between">
              <span className="font-medium">獎金抽成比例</span>
              <span className="text-[#FF9500] font-semibold tabular-nums">
                {jackpot.contributionPercent}%
              </span>
            </div>
          }
          subtitle={
            <div className="mt-2">
              <Range
                value={jackpot.contributionPercent}
                min={0.5}
                max={3}
                step={0.1}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setJackpot((prev) => ({
                    ...prev,
                    contributionPercent: parseFloat(parseFloat(e.target.value).toFixed(1)),
                  }))
                }
              />
              <div className="flex justify-between text-[11px] text-[#C7C7CC] mt-1">
                <span>0.5%</span>
                <span>3%</span>
              </div>
            </div>
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center">
              <Percent className="w-4 h-4 text-[#FF9500]" />
            </div>
          }
        />

        {/* Min Trigger Hand */}
        <ListInput
          label="最低觸發手牌"
          type="text"
          placeholder="同花順"
          value={jackpot.minTriggerHand}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setJackpot((prev) => ({ ...prev, minTriggerHand: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#AF52DE]/15 flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#AF52DE]" />
            </div>
          }
        />

        {/* Pool Cap */}
        <ListInput
          label="獎金池上限"
          type="number"
          placeholder="100000"
          value={jackpot.poolCap}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setJackpot((prev) => ({ ...prev, poolCap: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FFD700]/15 flex items-center justify-center">
              <Trophy className="w-4 h-4 text-[#FFD700]" />
            </div>
          }
          info="單位：USD"
        />
      </List>

      {/* ========== Security Settings ========== */}
      <BlockTitle className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#FF3B30]" />
        安全設定
      </BlockTitle>

      <List strongIos insetIos>
        {/* Max Deposit */}
        <ListInput
          label="最大單筆儲值"
          type="number"
          placeholder="10000"
          value={security.maxDeposit}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSecurity((prev) => ({ ...prev, maxDeposit: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-[#34C759]" />
            </div>
          }
          info="單位：USD"
        />

        {/* Max Withdrawal */}
        <ListInput
          label="最大單筆提款"
          type="number"
          placeholder="5000"
          value={security.maxWithdrawal}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSecurity((prev) => ({ ...prev, maxWithdrawal: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center">
              <ArrowDownToLine className="w-4 h-4 text-[#FF9500]" />
            </div>
          }
          info="單位：USD"
        />

        {/* Daily Withdrawal Limit */}
        <ListInput
          label="每日提款上限"
          type="number"
          placeholder="20000"
          value={security.dailyWithdrawalLimit}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSecurity((prev) => ({ ...prev, dailyWithdrawalLimit: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/15 flex items-center justify-center">
              <CalendarClock className="w-4 h-4 text-[#FF3B30]" />
            </div>
          }
          info="單位：USD"
        />

        {/* IP Restriction */}
        <ListItem
          media={
            <div className="w-8 h-8 rounded-lg bg-[#8E8E93]/15 flex items-center justify-center">
              <Globe className="w-4 h-4 text-[#8E8E93]" />
            </div>
          }
          title={<span className="font-medium">IP 限制</span>}
          after={
            <Toggle
              checked={security.ipRestriction}
              onChange={() =>
                setSecurity((prev) => ({ ...prev, ipRestriction: !prev.ipRestriction }))
              }
            />
          }
        />

        {/* Two Factor Auth */}
        <ListItem
          media={
            <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#007AFF]" />
            </div>
          }
          title={<span className="font-medium">雙重驗證</span>}
          after={
            <Toggle
              checked={security.twoFactorAuth}
              onChange={() =>
                setSecurity((prev) => ({ ...prev, twoFactorAuth: !prev.twoFactorAuth }))
              }
            />
          }
        />
      </List>

      {/* ========== System Settings ========== */}
      <BlockTitle className="flex items-center gap-2">
        <Wifi className="w-4 h-4 text-[#AF52DE]" />
        系統設定
      </BlockTitle>

      <List strongIos insetIos>
        {/* WS Heartbeat */}
        <ListInput
          label="WebSocket 心跳間隔"
          type="number"
          placeholder="30"
          value={system.wsHeartbeat}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSystem((prev) => ({ ...prev, wsHeartbeat: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#AF52DE]/15 flex items-center justify-center">
              <Wifi className="w-4 h-4 text-[#AF52DE]" />
            </div>
          }
          info="單位：秒"
        />

        {/* Disconnect Timeout */}
        <ListInput
          label="斷線超時"
          type="number"
          placeholder="60"
          value={system.disconnectTimeout}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSystem((prev) => ({ ...prev, disconnectTimeout: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/15 flex items-center justify-center">
              <Timer className="w-4 h-4 text-[#FF3B30]" />
            </div>
          }
          info="單位：秒"
        />

        {/* Hand Timeout */}
        <ListInput
          label="手牌超時"
          type="number"
          placeholder="30"
          value={system.handTimeout}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSystem((prev) => ({ ...prev, handTimeout: e.target.value }))
          }
          media={
            <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center">
              <Clock className="w-4 h-4 text-[#FF9500]" />
            </div>
          }
          info="單位：秒"
        />
      </List>

      {/* Save Button */}
      <Block className="!px-4 !mt-6 !mb-4">
        <Button large onClick={() => setShowSaveDialog(true)}>
          <Save className="w-4 h-4 mr-1.5 inline" />
          儲存設定
        </Button>
      </Block>

      {/* Bottom padding */}
      <div className="h-8" />

      {/* Save Confirmation Dialog */}
      <Dialog
        opened={showSaveDialog}
        onBackdropClick={() => setShowSaveDialog(false)}
        title="儲存設定"
        content={
          <p className="text-[13px] text-[#8E8E93] mt-2">
            確定要儲存所有變更嗎？部分設定將立即生效。
          </p>
        }
        buttons={
          <>
            <DialogButton onClick={() => setShowSaveDialog(false)}>
              取消
            </DialogButton>
            <DialogButton strong onClick={handleSave}>
              確認儲存
            </DialogButton>
          </>
        }
      />

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-2 bg-black/80 text-white px-5 py-3 rounded-full shadow-lg">
            <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
            <span className="text-sm font-medium">設定已儲存</span>
          </div>
        </div>
      )}
    </>
  )
}
